import React, { FC } from "react";
import { Box, Link as MuiLink, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link } from "react-router-dom";
import {
  LEG_LABEL,
  LiveBoardTrip,
  fmtAgo,
  fmtLocal,
  fmtRel,
  hasBackhaul,
  reasonsFor,
  severityOf,
  targetOf,
  tripHref,
} from "map/liveBoard";
import { colorFor } from "./palette";

/**
 * The hover card. The short form of the detail panel -- no train number, no timeline -- with one
 * job the detail panel does not have: its first line says why the mark is red or amber, so a
 * dispatcher scanning the map gets the answer without clicking.
 */
const TripTooltip: FC<{ trip: LiveBoardTrip }> = ({ trip: t }) => {
  const color = colorFor(t);
  const sev = severityOf(t);
  const why = reasonsFor(t);
  const tgt = targetOf(t);

  const rows: [string, string][] = [
    ["Status", `${t.trip_status}${t.dispatch_status !== "Dispatched" ? ` · ${t.dispatch_status}` : ""}`],
    ["Driver", t.driver_name ? `${t.driver_name}${t.driver_cell ? ` · ${t.driver_cell}` : ""}` : "not assigned"],
    ["Vehicle", t.vehicle_unit ? `Unit ${t.vehicle_unit} · ${t.vehicle_description || ""}` : "not assigned"],
    ["Hub", `${t.hub}${t.hub_state && !t.hub?.includes(t.hub_state) ? `, ${t.hub_state}` : ""} · ${t.client}`],
    ["Pickup", `${t.pickup_name} · ${fmtLocal(t.pickup_local)} ${t.trip_tz} (${fmtRel(t.minutes_to_pickup)})`],
    ["Dropoff", `${t.dropoff_name}${t.planned_miles ? ` · ${t.planned_miles} mi` : ""}`],
    ["Last event", t.last_event ? `${t.last_event} · ${fmtAgo(t.minutes_since_last_event)}` : "none yet"],
  ];
  if (hasBackhaul(t)) rows.push(["Backhaul", `${t.backhaul_hubs} within 15 min of dropoff`]);
  if (t.leg !== "pending") {
    rows.push([
      "Vehicle GPS",
      t.vehicle_gps_age_min == null
        ? "no GPS row"
        : `${fmtAgo(t.vehicle_gps_age_min)}${
            t.vehicle_stopped_min > 1 ? ` · stopped ${fmtRel(-t.vehicle_stopped_min).replace(" ago", "")}` : " · moving"
          }`,
    ]);
  }
  // Which of the five candidate positions the query settled on -- the difference between a van
  // that is really there and one that is drawn at its last event.
  rows.push([
    "Pin",
    t.leg === "pending"
      ? "pickup point"
      : `${(t.current_loc_source || "").replace("_", " ")}${tgt ? ` → ${tgt.kind}` : ""}`,
  ]);
  if (t.bad_coords?.length) rows.push(["Invalid coords", t.bad_coords.join(" · ")]);

  // The height cap is the backstop to the popup's own anchor flipping: a trip carrying several
  // reasons plus invalid coords can outgrow the map whichever side it opens on, and a card taller
  // than its container gets cut off rather than repositioned. Scrolls inside itself at that
  // point, which beats losing the top rows.
  return (
    <Box sx={{ minWidth: 250, fontSize: 12, maxHeight: "55vh", overflowY: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 13, color }}>Trip #{t.tripID}</Typography>
        <Box
          component="span"
          sx={{ fontSize: 10, px: 0.6, py: 0.1, borderRadius: 0.5, bgcolor: color, color: "#fff", fontWeight: 600 }}
        >
          {LEG_LABEL[t.leg]}
        </Box>
        {/* The card is reachable now (the map holds it open while the pointer is over it), so the
            way into the trip belongs here rather than only in the detail panel behind a click on
            the mark. Same target as the panel's link, opened in its own tab so the board keeps
            polling behind it. */}
        <MuiLink
          component={Link}
          to={tripHref(t)}
          target="_blank"
          rel="noopener"
          sx={{ ml: "auto", display: "inline-flex", alignItems: "center", gap: 0.25, fontSize: 11 }}
        >
          See details <OpenInNewIcon sx={{ fontSize: 11 }} />
        </MuiLink>
      </Box>
      {why.length ? (
        <Box sx={{ mb: 0.5, fontSize: 11.5, color, fontWeight: 500 }}>
          <b>{sev === "late" ? "Late" : "Warning"}</b> · {why.join(" · ")}
        </Box>
      ) : null}
      <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 1, rowGap: 0.25 }}>
        {rows.map(([k, v]) => (
          <React.Fragment key={k}>
            <Box sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>{k}</Box>
            <Box>{v}</Box>
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

export default TripTooltip;
